import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../api/client';
import { useNotification } from '../components/Notification';
import ConfirmModal from '../components/ConfirmModal';
import Pagination from '../components/Pagination';
import { EditIcon, DeleteIcon, ActivateIcon, DeactivateIcon, UsersStatIcon } from '../components/Icons';
import './AdminUsers.css';

function AdminUsers() {
  const queryClient = useQueryClient();
  const notification = useNotification();
  const [confirmModal, setConfirmModal] = useState({ 
    isOpen: false, 
    onConfirm: null, 
    message: '', 
    title: '',
    type: 'warning'
  });
  const [filters, setFilters] = useState({
    search: '',
    status: 'all', // 'all', 'active', 'inactive'
    role: 'all', // 'all', 'admin', 'user'
  });
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    email: '',
    is_active: true,
    is_admin: false,
    email_verified: false,
  });
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ new_password: '', confirm_password: '' });

  const { data, isLoading } = useQuery({
    queryKey: ['admin-users', filters, page, limit],
    queryFn: async () => {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit === 'all' ? '999999' : limit.toString(),
      });
      
      if (filters.search) params.append('search', filters.search);
      if (filters.status !== 'all') params.append('status', filters.status);
      if (filters.role !== 'all') params.append('role', filters.role);

      const res = await apiClient.get(`/admin/users?${params.toString()}`);
      return res.data;
    },
  });

  const updateUserMutation = useMutation({
    mutationFn: async ({ id, data }) => {
      return apiClient.put(`/admin/users/${id}`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['admin-users']);
      setShowEditModal(false);
      setEditingUser(null);
      notification.success('Vartotojo duomenys sėkmingai atnaujinti');
    },
    onError: (error) => {
      notification.error(error.response?.data?.error || 'Klaida atnaujinant vartotoją');
    },
  });

  const resetPasswordMutation = useMutation({
    mutationFn: async ({ id, password }) => {
      return apiClient.post(`/admin/users/${id}/reset-password`, { new_password: password });
    },
    onSuccess: () => {
      setShowPasswordModal(false);
      setPasswordForm({ new_password: '', confirm_password: '' });
      notification.success('Slaptažodis sėkmingai pakeistas');
    },
    onError: (error) => {
      notification.error(error.response?.data?.error || 'Klaida keičiant slaptažodį');
    },
  });

  const deleteUserMutation = useMutation({
    mutationFn: async (id) => {
      return apiClient.delete(`/admin/users/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['admin-users']);
      notification.success('Vartotojas sėkmingai pašalintas');
    },
    onError: (error) => {
      notification.error(error.response?.data?.error || 'Klaida šalinant vartotoją');
    },
  });

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setEditForm({
      first_name: user.first_name || '',
      last_name: user.last_name || '',
      phone: user.phone || '',
      email: user.email || '',
      is_active: user.is_active === 1 || user.is_active === true,
      is_admin: user.is_admin === 1 || user.is_admin === true,
      email_verified: user.email_verified === 1 || user.email_verified === true,
    });
    setShowEditModal(true);
  };

  const handleSave = () => {
    if (!editingUser) return;
    updateUserMutation.mutate({ id: editingUser.id, data: editForm });
  };

  const handleResetPassword = () => {
    if (!passwordForm.new_password || passwordForm.new_password.length < 6) {
      notification.warning('Slaptažodis turi būti bent 6 simbolių');
      return;
    }
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      notification.warning('Slaptažodžiai nesutampa');
      return;
    }
    if (!editingUser) return;
    resetPasswordMutation.mutate({ id: editingUser.id, password: passwordForm.new_password });
  };

  const handleDelete = (user) => {
    setConfirmModal({
      isOpen: true,
      title: 'Patvirtinti vartotojo pašalinimą',
      message: `Ar tikrai norite pašalinti vartotoją ${user.email}? Šis veiksmas negrįžtamas.`,
      type: 'error',
      onConfirm: () => {
        deleteUserMutation.mutate(user.id);
        setConfirmModal({ isOpen: false, onConfirm: null, message: '', title: '', type: 'warning' });
      },
    });
  };

  const handleToggleActive = (user) => {
    updateUserMutation.mutate({ 
      id: user.id, 
      data: { is_active: !(user.is_active === 1 || user.is_active === true) } 
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('lt-LT', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    return parseFloat(amount || 0).toFixed(2) + ' €';
  };

  if (isLoading) {
    return <div className="admin-users-page">Kraunama...</div>;
  }

  const users = data?.users || [];
  const pagination = data?.pagination || { page: 1, limit: 25, total: 0, totalPages: 1 };

  return (
    <div className="admin-users-page">
      <div className="admin-header">
        <div className="header-title-section">
          <h1>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
              <UsersStatIcon /> Vartotojai
            </span>
          </h1>
          <p className="header-subtitle">Valdykite ir sekite visus sistemos vartotojus</p>
        </div>
      </div>

      {/* Filtrai */}
      <div className="filters-section">
        <div className="filters-grid">
          <div className="filter-group">
            <label>Paieška:</label>
            <input
              type="text"
              placeholder="El. paštas, vardas, pavardė, telefonas..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              className="input"
            />
          </div>
          <div className="filter-group">
            <label>Statusas:</label>
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="input"
            >
              <option value="all">Visi statusai</option>
              <option value="active">Aktyvūs</option>
              <option value="inactive">Neaktyvūs</option>
            </select>
          </div>
          <div className="filter-group">
            <label>Rolė:</label>
            <select
              value={filters.role}
              onChange={(e) => handleFilterChange('role', e.target.value)}
              className="input"
            >
              <option value="all">Visos rolės</option>
              <option value="admin">Administratoriai</option>
              <option value="user">Vartotojai</option>
            </select>
          </div>
          <div className="filter-group">
            <button
              onClick={() => {
                setFilters({ search: '', status: 'all', role: 'all' });
                setPage(1);
              }}
              className="btn btn-secondary"
            >
              Išvalyti
            </button>
          </div>
        </div>
      </div>

      {/* Statistika */}
      <div className="stats-section">
        <div className="stat-card">
          <div className="stat-label">Iš viso vartotojų</div>
          <div className="stat-value">{pagination.total}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Aktyvūs</div>
          <div className="stat-value">{users.filter(u => u.is_active === 1 || u.is_active === true).length}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Administratoriai</div>
          <div className="stat-value">{users.filter(u => u.is_admin === 1 || u.is_admin === true).length}</div>
        </div>
      </div>

      {/* Vartotojų sąrašas */}
      <div className="users-table-wrapper">
        <table className="users-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>El. paštas</th>
              <th>Vardas, pavardė</th>
              <th>Telefonas</th>
              <th>Rolė</th>
              <th>Statusas</th>
              <th>Užsakymai</th>
              <th>Išleista</th>
              <th>Paskutinis prisijungimas</th>
              <th>Registracija</th>
              <th>Veiksmai</th>
            </tr>
          </thead>
          <tbody>
            {users.length === 0 ? (
              <tr>
                <td colSpan="11" className="empty-state">
                  Vartotojų nerasta
                </td>
              </tr>
            ) : (
              users.map((user) => (
                <tr key={user.id}>
                  <td>{user.id}</td>
                  <td>
                    <div className="email-cell">
                      {user.email}
                      {user.email_verified === 1 || user.email_verified === true ? (
                        <span className="verified-badge" title="El. paštas patvirtintas">✓</span>
                      ) : (
                        <span className="unverified-badge" title="El. paštas nepatvirtintas">!</span>
                      )}
                    </div>
                  </td>
                  <td>
                    {user.first_name || user.last_name 
                      ? `${user.first_name || ''} ${user.last_name || ''}`.trim() 
                      : '—'}
                  </td>
                  <td>{user.phone || '—'}</td>
                  <td>
                    <span className={`role-badge ${user.is_admin === 1 || user.is_admin === true ? 'role-admin' : 'role-user'}`}>
                      {user.is_admin === 1 || user.is_admin === true ? 'Admin' : 'Vartotojas'}
                    </span>
                  </td>
                  <td>
                    <span className={`status-badge ${user.is_active === 1 || user.is_active === true ? 'status-active' : 'status-inactive'}`}>
                      {user.is_active === 1 || user.is_active === true ? 'Aktyvus' : 'Neaktyvus'}
                    </span>
                  </td>
                  <td>
                    <div className="orders-info">
                      <div>Viso: {user.total_orders || 0}</div>
                      <div className="paid-orders">Apmokėta: {user.paid_orders || 0}</div>
                    </div>
                  </td>
                  <td>{formatCurrency(user.total_spent)}</td>
                  <td>{formatDate(user.last_login)}</td>
                  <td>{formatDate(user.created_at)}</td>
                  <td>
                    <div className="action-buttons">
                      <button
                        onClick={() => handleEdit(user)}
                        className="btn-icon btn-icon-secondary"
                        title="Redaguoti"
                      >
                        <EditIcon />
                      </button>
                      <button
                        onClick={() => handleToggleActive(user)}
                        className="btn-icon btn-icon-secondary"
                        title={user.is_active === 1 || user.is_active === true ? 'Deaktyvuoti' : 'Aktyvuoti'}
                      >
                        {user.is_active === 1 || user.is_active === true ? <DeactivateIcon /> : <ActivateIcon />}
                      </button>
                      <button
                        onClick={() => {
                          setEditingUser(user);
                          setPasswordForm({ new_password: '', confirm_password: '' });
                          setShowPasswordModal(true);
                        }}
                        className="btn-icon btn-icon-secondary"
                        title="Keisti slaptažodį"
                      >
                        🔑
                      </button>
                      {!(user.is_admin === 1 || user.is_admin === true) && (
                        <button
                          onClick={() => handleDelete(user)}
                          className="btn-icon btn-icon-danger"
                          title="Pašalinti"
                        >
                          <DeleteIcon />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pagination && (
        <Pagination
          page={pagination.page}
          totalPages={pagination.totalPages}
          totalItems={pagination.total}
          limit={limit === 'all' ? 'all' : parseInt(limit)}
          onPageChange={setPage}
          onLimitChange={(newLimit) => {
            setLimit(newLimit);
            setPage(1);
          }}
        />
      )}

      {/* Redagavimo modalas */}
      {showEditModal && editingUser && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Redaguoti vartotoją</h2>
              <button onClick={() => setShowEditModal(false)} className="btn-close">×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>El. paštas</label>
                <input
                  type="email"
                  value={editForm.email}
                  onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                  className="input"
                />
              </div>
              <div className="form-group">
                <label>Vardas</label>
                <input
                  type="text"
                  value={editForm.first_name}
                  onChange={(e) => setEditForm({ ...editForm, first_name: e.target.value })}
                  className="input"
                />
              </div>
              <div className="form-group">
                <label>Pavardė</label>
                <input
                  type="text"
                  value={editForm.last_name}
                  onChange={(e) => setEditForm({ ...editForm, last_name: e.target.value })}
                  className="input"
                />
              </div>
              <div className="form-group">
                <label>Telefonas</label>
                <input
                  type="tel"
                  value={editForm.phone}
                  onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                  className="input"
                />
              </div>
              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={editForm.is_active}
                    onChange={(e) => setEditForm({ ...editForm, is_active: e.target.checked })}
                  />
                  Aktyvus
                </label>
              </div>
              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={editForm.is_admin}
                    onChange={(e) => setEditForm({ ...editForm, is_admin: e.target.checked })}
                  />
                  Administratorius
                </label>
              </div>
              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={editForm.email_verified}
                    onChange={(e) => setEditForm({ ...editForm, email_verified: e.target.checked })}
                  />
                  El. paštas patvirtintas
                </label>
              </div>
            </div>
            <div className="modal-footer">
              <button onClick={() => setShowEditModal(false)} className="btn btn-secondary">
                Atšaukti
              </button>
              <button onClick={handleSave} className="btn btn-primary" disabled={updateUserMutation.isLoading}>
                {updateUserMutation.isLoading ? 'Išsaugoma...' : 'Išsaugoti'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Slaptažodžio keitimo modalas */}
      {showPasswordModal && editingUser && (
        <div className="modal-overlay" onClick={() => setShowPasswordModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Keisti slaptažodį</h2>
              <button onClick={() => setShowPasswordModal(false)} className="btn-close">×</button>
            </div>
            <div className="modal-body">
              <p>Naujas slaptažodis vartotojui: <strong>{editingUser.email}</strong></p>
              <div className="form-group">
                <label>Naujas slaptažodis</label>
                <input
                  type="password"
                  value={passwordForm.new_password}
                  onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
                  className="input"
                  placeholder="Bent 6 simbolių"
                />
              </div>
              <div className="form-group">
                <label>Patvirtinti slaptažodį</label>
                <input
                  type="password"
                  value={passwordForm.confirm_password}
                  onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })}
                  className="input"
                  placeholder="Pakartokite slaptažodį"
                />
              </div>
            </div>
            <div className="modal-footer">
              <button onClick={() => setShowPasswordModal(false)} className="btn btn-secondary">
                Atšaukti
              </button>
              <button onClick={handleResetPassword} className="btn btn-primary" disabled={resetPasswordMutation.isLoading}>
                {resetPasswordMutation.isLoading ? 'Keičiama...' : 'Keisti slaptažodį'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Modal */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        type={confirmModal.type}
        onConfirm={() => {
          if (confirmModal.onConfirm) confirmModal.onConfirm();
          setConfirmModal({ isOpen: false, onConfirm: null, message: '', title: '', type: 'warning' });
        }}
        onCancel={() => setConfirmModal({ isOpen: false, onConfirm: null, message: '', title: '', type: 'warning' })}
      />
    </div>
  );
}

export default AdminUsers;
